import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarCheck, Loader2, ShieldCheck, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const RETURN_KEY = "lf_return_to";

export default function OAuthConsent() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tx = params.get("tx") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      sessionStorage.setItem(RETURN_KEY, `/oauth/consent?tx=${encodeURIComponent(tx)}`);
      nav("/login", { replace: true });
      return;
    }
    sessionStorage.removeItem(RETURN_KEY);
    if (!tx) {
      setError("This connection link is missing information. Start again from ChatGPT.");
      return;
    }
    api.get(`/oauth/consent/${encodeURIComponent(tx)}`)
      .then(({ data }) => { setInfo(data); setPicked(data.scopes.map((s) => s.scope)); })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This request is no longer valid."));
  }, [user, loading, tx, nav]);

  const decide = async (approve) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/oauth/consent/${encodeURIComponent(tx)}`, { approve, scopes: picked });
      window.location.assign(data.redirect);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
      setBusy(false);
    }
  };

  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6" data-testid="oauth-consent-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-8 card-crisp">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-[#FF5A00] flex items-center justify-center"><Zap className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
          <span className="font-heading font-bold text-lg">LeadForge</span>
        </div>
        {error ? (
          <p className="text-sm text-rose-600" data-testid="oauth-consent-error">{error}</p>
        ) : !info ? (
          <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold mb-1">Connect {info.client_name}</h1>
            <p className="text-sm text-zinc-600 mb-6">
              {info.client_name} wants access to <b>{info.business_name}</b> as <span data-testid="oauth-consent-user">{info.user_email}</span>.
            </p>
            {!info.can_connect ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mb-4" data-testid="oauth-consent-role-warning">
                Only owners and admins can connect ChatGPT. Ask your business owner to connect it.
              </p>
            ) : (
              <div className="space-y-3 mb-6">
                {info.scopes.map((s) => (
                  <label key={s.scope} className="flex items-start gap-3 text-sm" data-testid={`oauth-scope-${s.scope}`}>
                    <Checkbox checked={picked.includes(s.scope)} disabled={s.required} onCheckedChange={() => toggle(s.scope)} className="mt-0.5" data-testid={`oauth-scope-checkbox-${s.scope}`} />
                    <span>
                      <span className="block font-medium">{s.description}</span>
                      <span className="font-mono-accent text-[11px] text-zinc-500">{s.scope}{s.required ? " · required" : ""}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-zinc-500 mb-6">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              Stays connected until you disconnect it in Settings. Customers are never texted unless you ask.
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" disabled={busy} onClick={() => decide(false)} data-testid="oauth-consent-deny">Cancel</Button>
              <Button className="flex-1 bg-[#FF5A00] hover:bg-[#e65100] text-white" disabled={busy || !info.can_connect} onClick={() => decide(true)} data-testid="oauth-consent-allow">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarCheck className="w-4 h-4 mr-1" />Allow</>}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const peekReturnTo = () => {
  const r = sessionStorage.getItem(RETURN_KEY);
  return r && r.startsWith("/") && !r.startsWith("//") ? r : null;
};

export const popReturnTo = () => {
  const r = peekReturnTo();
  sessionStorage.removeItem(RETURN_KEY);
  return r;
};
